import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import DataTable from "../ui/DataTable";
import Pagination from "../ui/Pagination";
import ConfirmDialog from "../ui/ConfirmDialog";
import { customizationService } from "../../services/customizationService";
import { projectService } from "../../services/projectService";
import toast from "react-hot-toast";

const MAX_NAME = 25;
const MAX_DESC = 100;
const COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const ResultStatusesTab = ({ clientId }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [colorHex, setColorHex] = useState("#a0a0a0");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [ignoredInMetrics, setIgnoredInMetrics] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remapDialog, setRemapDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [remapToId, setRemapToId] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await projectService.getProjects({
        client_id: clientId,
        page: 1,
        limit: 500,
        is_active: true,
        is_deleted: false,
      });
      const list = res?.data?.projects || res?.projects || [];
      setProjects(list);
      if (list.length && !projectId) setProjectId(list[0].project_id);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load projects");
    }
  }, [clientId]);

  const loadStatuses = useCallback(async () => {
    if (!clientId || !projectId) return;
    setLoading(true);
    try {
      const res = await customizationService.resultStatusesList(projectId);
      setStatuses(res?.data?.result_statuses || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load result statuses");
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setCode("");
    setColorHex("#a0a0a0");
    setDescription("");
    setIsDefault(false);
    setIsFinal(false);
    setIgnoredInMetrics(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setName(row.name || "");
    setCode(row.code || "");
    setColorHex(row.color_hex || "#a0a0a0");
    setDescription("");
    setIsDefault(!!row.is_default);
    setIsFinal(!!row.is_final);
    setIgnoredInMetrics(!!row.ignored_in_metrics);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Name is required");
      return;
    }
    if (n.length > MAX_NAME) {
      toast.error(`Name must be at most ${MAX_NAME} characters`);
      return;
    }
    if (!COLOR_REGEX.test(colorHex)) {
      toast.error("Color must be a valid hex (#RRGGBB)");
      return;
    }
    const c = (code || n)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .slice(0, 30);
    setSaving(true);
    try {
      const payload = {
        name: n,
        code: c,
        color_hex: colorHex,
        is_default: isDefault,
        is_final: isFinal,
        ignored_in_metrics: ignoredInMetrics,
      };
      if (editId) {
        await customizationService.resultStatusesUpdate(
          projectId,
          editId,
          payload,
        );
        toast.success("Result status updated");
      } else {
        await customizationService.resultStatusesCreate(projectId, payload);
        toast.success("Result status created");
      }
      setModalOpen(false);
      loadStatuses();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setSelectedStatus(row);
    const others = statuses.filter((s) => s.id !== row.id);
    if (others.length > 0) {
      setRemapToId(others.find((s) => s.is_default)?.id || others[0].id);
      setRemapDialog(true);
    } else {
      toast.error("Cannot delete the only status. Add another first.");
    }
  };

  const confirmDelete = async () => {
    if (!selectedStatus || !remapToId) return;
    setDeleteLoading(true);
    try {
      await customizationService.resultStatusesDelete(
        projectId,
        selectedStatus.id,
        remapToId,
      );
      toast.success("Result status deleted");
      setRemapDialog(false);
      setSelectedStatus(null);
      loadStatuses();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {/* <span
            className="w-4 h-4 rounded-full border border-gray-300 shrink-0"
            style={{ backgroundColor: row.color_hex || "#ccc" }}
          /> */}
          <span className="font-medium">{row.name}</span>
          {row.is_default && (
            <Badge variant="outline" className="text-xs">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "code", header: "Code", render: (_, row) => <span className="font-medium">{row.code}</span>
    },
    {
      key: "color", header: "Color", render: (_, row) => <div style={{ height: "12px", width: "12px", borderRadius: "50%", background: row?.color_hex }} />
    },
    {
      key: "type", header: "System/Custom", render: (_, row) => <span className="font-medium">{row.is_system ? "System" : "Custom"}</span>
    },
    {
      key: "options",
      header: "Options",
      render: (_, row) => (
        <div className="flex gap-1 flex-wrap">
          {row.is_final && <Badge variant="outline">Final</Badge>}
          {row.ignored_in_metrics && (
            <Badge variant="outline">Ignored in metrics</Badge>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="w-4 h-4" />
          </Button>
          {!row.is_default && (
            <Button
              variant="ghost"
              size="icon"
              className="text-red-600"
              onClick={() => openDelete(row)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const data = useMemo(() => statuses?.slice((currentPage - 1) * 5, 5 * (currentPage)), [currentPage, statuses])


  return (
    <div className="space-y-3">
   
      <div className="flex" style={{flexDirection:"row-reverse",justifyContent:"space-between"}}>
   <div className="flex gap-2">
      <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.name || p.project_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        <Button onClick={openCreate} disabled={!projectId}>
        <Plus className="w-4 h-4 mr-2" />
        Add New Result Status
      </Button>
      </div>


    
      <div className="flex gap-2" style={{alignItems:"center"}}>
        <strong>Please Note:{" "}</strong>
        <p className="text-gray-600 text-sm ">
          Maximum of  12 Result Status are allowed to a
        </p>
      </div>
      
      </div>
      <div className="card overflow-hidden">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage={
            projectId
              ? "No result statuses. Add one above."
              : "Select a project first."
          }
        />
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(statuses.length / 5)}
        totalItems={statuses.length}
        itemsPerPage={5}
        onPageChange={(page) => setCurrentPage(page)}
      />
   

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editId ? "Edit Result Status" : "Add New Result Status"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">

            {/* Name */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Name *</label>
                <span className="text-xs text-gray-400">{name.length} / {MAX_NAME}</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Untested"
                maxLength={MAX_NAME}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Description *</label>
              </div>
              <div className="relative">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your custom field"
                  maxLength={MAX_DESC}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white resize-none"
                />
                <span className="absolute bottom-2 right-3 text-xs text-gray-400">
                  {description.length} / {MAX_DESC}
                </span>
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Color *</label>
              <div className="flex items-center gap-2 w-64 px-3 py-2 border border-gray-300 rounded-md bg-white">
                <input
                  type="text"
                  value={colorHex.replace('#', '')}
                  onChange={(e) => setColorHex('#' + e.target.value.replace('#', ''))}
                  placeholder="a0a0a0"
                  maxLength={7}
                  className="flex-1 text-sm focus:outline-none bg-transparent"
                />
                {/* colour wheel trigger */}
                <label className="cursor-pointer flex-shrink-0" title="Pick colour">
                  <input
                    type="color"
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    className="sr-only"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400 hover:text-gray-600" viewBox="0 0 24 24" fill="none" stroke={colorHex} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M12 2a10 10 0 0 1 0 20" />
                    <path d="M2 12h20" /><path d="M12 2c2.76 0 5 4.48 5 10s-2.24 10-5 10" />
                  </svg>
                </label>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-4 pt-1">

              {/* Ignored in metrics */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ignoredInMetrics}
                  onChange={(e) => setIgnoredInMetrics(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-400 text-indigo-600 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-bold text-gray-800">This status is ignored in metrics.</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Ignored statuses are not included in the generation of metrics in charts, dashboard, and reports.
                  </p>
                </div>
              </label>

              {/* Final status */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isFinal}
                  onChange={(e) => setIsFinal(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-400 text-indigo-600 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-bold text-gray-800">This status is a final status.</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tests with a final status are considered completed
                    and add to the overall progress of a test run on the Progress tabs (such as Passed, Failed or Blocked).
                  </p>
                </div>
              </label>

              {/* Default status */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-400 text-indigo-600 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-bold text-gray-800">This status is the default status.</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    The default case type is the pre-selected type for new cases and the fallback type when you delete other case types.
                    Only one type can be selected as the default.
                  </p>
                </div>
              </label>

            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={remapDialog} onOpenChange={setRemapDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete &amp; Remap</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Executions with &quot;{selectedStatus?.name}&quot; will be remapped
            to the selected status.
          </p>
          <div>
            <Label>Remap to</Label>
            <Select value={remapToId} onValueChange={setRemapToId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses
                  .filter((s) => s.id !== selectedStatus?.id)
                  .map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRemapDialog(false);
                setSelectedStatus(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete & Remap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResultStatusesTab;
